import React, { Component } from 'react'
import {
    Alert, Card, Table, Button, Tag, Space, Popconfirm, message, Select, Typography
} from 'antd'
import {
    getApiCredentialList, createApiCredential,
    disableApiCredential, enableApiCredential, deleteApiCredential,
    getUserListForDropbox
} from '../../request'
import { StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import moment from 'moment'

const { Text } = Typography

/**
 * API Key 管理
 * onlyMine: 为 true 时仅展示当前用户自己的 key，隐藏用户筛选器（个人中心场景）
 * fixedUserId: 锁定用户ID（onlyMine=true 时由父组件传入）
 *
 * 后端接口约定：
 *   - userId 为空  → 查询全部（管理员场景，后端需校验调用方具有管理员权限）
 *   - userId 有值  → 按该 userId 过滤（后端需校验调用方是该用户本人或管理员）
 */
export default class ApiKeyManagement extends Component {
    state = {
        dataSource: [],
        userList: [],
        columns: [],
        loading: false,
        creating: false,
        newKeyValue: '',
        autoCreateTried: false,
        formParam: {
            currentPage: 1,
            pageSize: 20,
        },
        total: 0,
    }

    componentDidMount() {
        const { fixedUserId } = this.props
        // columns 依赖 onlyMine prop，初始化一次存入 state，避免每次 render 重建
        this.setState({ columns: this.buildColumns() })
        if (fixedUserId) {
            this.setState(
                { formParam: { ...this.state.formParam, userId: fixedUserId } },
                () => this.initList()
            )
        } else {
            this.initList()
            this.initUserList()
        }
    }

    initList = () => {
        const { onlyMine } = this.props
        this.setState({ loading: true })
        getApiCredentialList()
            .then(res => {
                const dataSource = Array.isArray(res) ? res : []
                this.setState({
                    loading: false,
                    dataSource,
                }, () => {
                    if (onlyMine && !dataSource.length) {
                        this.ensureDefaultKey()
                    }
                })
            })
            .catch(() => this.setState({ loading: false }))
    }

    buildDefaultCredentialPayload = () => ({
        name: '默认 API Key',
        scope: 'mcp:invoke',
        scopes: ['mcp:invoke'],
        expireAt: null,
        status: 1,
    })

    getCreateCredentialErrorMessage = (error) => {
        if (!error) return 'API Key 自动创建失败'
        if (typeof error === 'string') return error
        if (error.message) return error.message
        if (error.error) return error.error
        if (error.response?.data?.message) return error.response.data.message
        if (error.response?.data?.error) return error.response.data.error
        return 'API Key 自动创建失败'
    }

    createDefaultCredential = async () => createApiCredential(this.buildDefaultCredentialPayload())

    ensureDefaultKey = () => {
        const { autoCreateTried, creating } = this.state
        if (autoCreateTried || creating) return

        this.setState({ autoCreateTried: true, creating: true })
        this.createDefaultCredential()
            .then(res => {
                this.setState({
                    creating: false,
                    newKeyValue: res?.fullKey || '',
                })
                message.success('已自动创建默认 API Key')
                this.initList()
            })
            .catch((error) => {
                this.setState({ creating: false })
                message.error(this.getCreateCredentialErrorMessage(error))
            })
    }

    initUserList = () => {
        getUserListForDropbox({ type: 0 }).then(res => {
            this.setState({ userList: res || [] })
        })
    }

    handleDisable = (record) => {
        disableApiCredential({ id: record.id }).then(() => {
            message.success('已禁用')
            this.initList()
        })
    }

    handleEnable = (record) => {
        enableApiCredential({ id: record.id }).then(() => {
            message.success('已启用')
            this.initList()
        })
    }

    handleDelete = (record) => {
        deleteApiCredential({ id: record.id }).then(() => {
            message.success('已删除')
            this.initList()
        })
    }

    buildColumns = () => {
        const { onlyMine } = this.props
        const cols = [
            {
                title: '名称',
                dataIndex: 'name',
                key: 'name',
                width: 150,
            },
            {
                title: 'Key前缀',
                dataIndex: 'keyDisplay',
                key: 'keyDisplay',
                width: 160,
                render: (text) => <Text code>{text}</Text>,
            },
            {
                title: 'Scope',
                dataIndex: 'scopes',
                key: 'scopes',
                render: (scopes) => {
                    if (!scopes) return '-'
                    let list = []
                    try {
                        list = Array.isArray(scopes) ? scopes : JSON.parse(scopes)
                    } catch (e) {
                        return <Text type="secondary">{String(scopes)}</Text>
                    }
                    return list.map(s => <Tag key={s} color="blue">{s}</Tag>)
                },
            },
            {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                width: 90,
                render: (status) =>
                    status === 1
                        ? <Tag color="success" icon={<CheckCircleOutlined />}>启用</Tag>
                        : <Tag color="default" icon={<StopOutlined />}>禁用</Tag>,
            },
            {
                title: '最近使用',
                dataIndex: 'lastUsedAt',
                key: 'lastUsedAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
            {
                title: '过期时间',
                dataIndex: 'expireAt',
                key: 'expireAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '永不过期',
            },
            {
                title: '创建时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
        ]
        if (!onlyMine) {
            cols.splice(2, 0, {
                title: '所属用户',
                dataIndex: 'userNick',
                key: 'userNick',
                width: 120,
            })
        }
        cols.push({
            title: '操作',
            key: 'actions',
            fixed: 'right',
            width: 160,
            render: (_, record) => (
                <Space size={4}>
                    {record.status === 1 ? (
                        <Popconfirm title="确认禁用该Key？" onConfirm={() => this.handleDisable(record)}>
                            <Button size="small" danger>禁用</Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm title="确认启用该Key？" onConfirm={() => this.handleEnable(record)}>
                            <Button size="small" type="primary" ghost>启用</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm title="确认删除该Key？删除后不可恢复" onConfirm={() => this.handleDelete(record)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        })
        return cols
    }

    render() {
        const { onlyMine } = this.props
        const { dataSource, columns, loading, creating, newKeyValue, userList, total } = this.state

        return (
            <div>
                <Card
                    title="API Key 管理"
                    bordered={false}
                >
                    {newKeyValue ? (
                        <Alert
                            style={{ marginBottom: 16 }}
                            type="success"
                            showIcon
                            message="系统已自动创建默认 API Key，请立即复制保存"
                            description={
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text copyable code style={{ wordBreak: 'break-all' }}>{newKeyValue}</Text>
                                    </div>
                                    <Text type="danger">该完整 Key 仅在本次页面中展示，请复制后妥善保存。</Text>
                                </div>
                            }
                        />
                    ) : null}

                    {/* 管理员模式显示用户筛选 */}
                    {!onlyMine && (
                        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                            <Select
                                style={{ width: 220 }}
                                showSearch
                                allowClear
                                placeholder="按用户筛选"
                                optionFilterProp="children"
                                fieldNames={{ label: 'USER_NICK', value: 'USER_ID' }}
                                filterOption={(input, option) =>
                                    ((option?.USER_NICK ?? '') + (option?.pinyin ?? '')).toLowerCase().includes(input.toLowerCase())
                                }
                                options={userList}
                                onChange={(val) => {
                                    this.setState(
                                        { formParam: { ...this.state.formParam, userId: val, currentPage: 1 } },
                                        this.initList
                                    )
                                }}
                            />
                            <Button onClick={this.initList} loading={loading || creating}>刷新</Button>
                        </div>
                    )}

                    <Table
                        bordered
                        size="small"
                        rowKey="id"
                        dataSource={dataSource}
                        columns={columns}
                        loading={loading || creating}
                        scroll={{ x: onlyMine ? 900 : 1100 }}
                        pagination={{
                            size: 'small',
                            current: this.state.formParam.currentPage,
                            pageSize: this.state.formParam.pageSize,
                            total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                this.setState(
                                    { formParam: { ...this.state.formParam, currentPage: page, pageSize } },
                                    this.initList
                                )
                            },
                        }}
                    />
                </Card>
            </div>
        )
    }
}
